import { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { patchTrip, patchRound } from '../lib/api';
import { toast } from 'sonner';
import { Trophy, CaretDown, CaretUp, Plus, Trash, Check, CurrencyGbp, Pencil } from '@phosphor-icons/react';

const CURRENCY_MAP = { GBP: '£', EUR: '€', USD: '$' };
const CURRENCY_OPTIONS = ['GBP', 'EUR', 'USD'];
const sym = (code) => CURRENCY_MAP[code] || code;

// Normalise numeric input: strip leading zeros, preserve decimals, allow empty for in-progress editing
const normaliseAmt = (str) => {
  if (str === '' || str === '-') return str;
  // Allow trailing decimal point or trailing zeros after decimal (in-progress typing like "5." or "5.0")
  if (/\.\d*$/.test(str) || str.endsWith('.')) {
    // Strip leading zeros before the integer part, but keep "0.x"
    return str.replace(/^0+(?=\d)/, '');
  }
  const n = Number(str);
  return isNaN(n) ? str : String(n);
};

export default function PrizePot({ trip, code, isOrganiser, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedRounds, setExpandedRounds] = useState({});

  const config = trip.pot_config;
  const currency = trip.currency || 'GBP';
  const players = (trip.players || []).filter(p => p.active !== false);
  const playerIds = new Set(players.map(p => p.id));
  const rounds = trip.rounds || [];

  // Edit state
  const [editCurrency, setEditCurrency] = useState(currency);
  const [editPerRound, setEditPerRound] = useState(config?.per_round_enabled ?? false);
  const [editPerRoundAmt, setEditPerRoundAmt] = useState(config?.per_round_amount ?? 0);
  const [editPerTrip, setEditPerTrip] = useState(config?.per_trip_enabled ?? false);
  const [editPerTripAmt, setEditPerTripAmt] = useState(config?.per_trip_amount ?? 0);
  const [editPayouts, setEditPayouts] = useState(config?.payout_structure ?? []);

  const resetEdit = () => {
    setEditCurrency(currency);
    setEditPerRound(config?.per_round_enabled ?? false);
    setEditPerRoundAmt(config?.per_round_amount ?? 0);
    setEditPerTrip(config?.per_trip_enabled ?? false);
    setEditPerTripAmt(config?.per_trip_amount ?? 0);
    setEditPayouts(config?.payout_structure ?? []);
  };

  if (!config && !isOrganiser) return null;

  // --- Computed summaries ---
  const tripPaid = (trip.pot_trip_paid || []).filter(id => playerIds.has(id));
  const tripPaidCount = tripPaid.length;
  const tripTotal = players.length;
  const tripCollected = tripPaidCount * (config?.per_trip_amount || 0);
  const tripExpected = tripTotal * (config?.per_trip_amount || 0);

  const roundSummary = (r) => {
    const paid = (r.pot_round_paid || []).filter(id => playerIds.has(id));
    const collected = paid.length * (config?.per_round_amount || 0);
    const expected = players.length * (config?.per_round_amount || 0);
    return { paid, count: paid.length, total: players.length, collected, expected };
  };

  const allRoundCollected = rounds.reduce((s, r) => s + roundSummary(r).collected, 0);
  const allRoundExpected = rounds.reduce((s, r) => s + roundSummary(r).expected, 0);

  // --- Toggle paid ---
  const toggleRoundPaid = async (roundId, playerId, currentlyPaid) => {
    const r = rounds.find(rd => rd.id === roundId);
    if (!r) return;
    const paid = [...(r.pot_round_paid || [])];
    const next = currentlyPaid ? paid.filter(id => id !== playerId) : [...paid, playerId];
    try {
      const res = await patchRound(code, roundId, { pot_round_paid: next });
      onUpdate(res.data);
    } catch { toast.error('Failed to update'); }
  };

  const toggleTripPaid = async (playerId, currentlyPaid) => {
    const paid = [...(trip.pot_trip_paid || [])];
    const next = currentlyPaid ? paid.filter(id => id !== playerId) : [...paid, playerId];
    try {
      const res = await patchTrip(code, { pot_trip_paid: next });
      onUpdate(res.data);
    } catch { toast.error('Failed to update'); }
  };

  // --- Save config ---
  const saveConfig = async () => {
    setSaving(true);
    try {
      const newConfig = {
        per_round_enabled: editPerRound,
        per_round_amount: parseFloat(editPerRoundAmt) || 0,
        per_trip_enabled: editPerTrip,
        per_trip_amount: parseFloat(editPerTripAmt) || 0,
        payout_structure: editPayouts,
      };
      const res = await patchTrip(code, { currency: editCurrency, pot_config: newConfig });
      onUpdate(res.data);
      setEditing(false);
      toast.success('Prize pot updated');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  // --- Enable pot (first time) ---
  const enablePot = async () => {
    setSaving(true);
    try {
      const res = await patchTrip(code, {
        pot_config: { per_round_enabled: true, per_round_amount: 10, per_trip_enabled: false, per_trip_amount: 0, payout_structure: [] },
      });
      onUpdate(res.data);
      setEditing(true);
      resetEdit();
    } catch { toast.error('Failed to enable'); }
    finally { setSaving(false); }
  };

  // --- Payout editor helpers ---
  const addPayout = () => setEditPayouts(prev => [...prev, { label: '', scope: 'round', type: 'fixed', value: 0, order: prev.length }]);
  const removePayout = (idx) => setEditPayouts(prev => prev.filter((_, i) => i !== idx));
  const updatePayout = (idx, field, val) => setEditPayouts(prev => prev.map((p, i) => i === idx ? { ...p, [field]: val } : p));

  // Payout amount display
  const payoutDisplay = (p) => {
    if (p.type === 'fixed') return `${sym(currency)}${p.value}`;
    const pool = p.scope === 'trip' ? tripCollected : allRoundCollected;
    const amt = (pool * (p.value / 100));
    return `${amt.toFixed(2)} (${p.value}% of ${sym(currency)}${pool.toFixed(2)})`;
  };

  // --- Collapsed summary ---
  const summaryParts = [];
  if (config?.per_round_enabled) summaryParts.push(`${sym(currency)}${config.per_round_amount}/round`);
  if (config?.per_trip_enabled) summaryParts.push(`${sym(currency)}${config.per_trip_amount}/trip`);

  // --- Not configured yet ---
  if (!config) {
    return (
      <Card className="bg-white border-[#E1E6E4] rounded-xl overflow-hidden" data-testid="prize-pot-card">
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#E59000]" weight="fill" />
            <span className="font-semibold text-sm text-[#1D242B]">Prize Pot</span>
          </div>
          {isOrganiser && (
            <Button size="sm" variant="outline" onClick={enablePot} disabled={saving}
              className="text-xs rounded-lg" data-testid="enable-pot-btn">
              Set Up
            </Button>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-[#E1E6E4] rounded-xl overflow-hidden" data-testid="prize-pot-card">
      {/* Header — always visible */}
      <button onClick={() => setExpanded(!expanded)} className="w-full p-4 flex items-center justify-between text-left"
        data-testid="prize-pot-toggle">
        <div className="flex items-center gap-2 min-w-0">
          <Trophy className="w-5 h-5 text-[#E59000] flex-shrink-0" weight="fill" />
          <span className="font-semibold text-sm text-[#1D242B]">Prize Pot</span>
          {summaryParts.length > 0 && (
            <span className="text-xs text-[#455561] ml-1">{summaryParts.join(' · ')}</span>
          )}
        </div>
        {expanded ? <CaretUp className="w-4 h-4 text-[#455561]" /> : <CaretDown className="w-4 h-4 text-[#455561]" />}
      </button>

      {expanded && (
        <div className="border-t border-[#E1E6E4]">
          {/* Edit button for organiser */}
          {isOrganiser && !editing && (
            <div className="px-4 pt-3 flex justify-end">
              <Button size="sm" variant="ghost" onClick={() => { resetEdit(); setEditing(true); }}
                className="text-xs gap-1 text-[#455561]" data-testid="edit-pot-btn">
                <Pencil className="w-3.5 h-3.5" /> Edit
              </Button>
            </div>
          )}

          {/* Per-Round Section */}
          {config.per_round_enabled && (
            <div className="px-4 py-3 space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-[#455561] uppercase tracking-wide">Per Round — {sym(currency)}{config.per_round_amount}</h4>
                <span className="text-xs text-[#455561]">
                  {sym(currency)}{allRoundCollected.toFixed(0)} of {sym(currency)}{allRoundExpected.toFixed(0)}
                </span>
              </div>
              {rounds.map(r => {
                const rs = roundSummary(r);
                const isExpRound = expandedRounds[r.id];
                return (
                  <div key={r.id} className="border border-[#E1E6E4] rounded-lg overflow-hidden">
                    <button onClick={() => setExpandedRounds(prev => ({ ...prev, [r.id]: !prev[r.id] }))}
                      className="w-full px-3 py-2 flex items-center justify-between text-left bg-[#FAFBFA]"
                      data-testid={`round-pot-toggle-${r.id}`}>
                      <span className="text-xs font-semibold text-[#1D242B]">{r.label}</span>
                      <div className="flex items-center gap-2">
                        <Badge className={`text-[10px] px-1.5 py-0 ${rs.count === rs.total ? 'bg-[#1B4332] text-white' : 'bg-[#F4F6F5] text-[#455561]'}`}>
                          {rs.count}/{rs.total}
                        </Badge>
                        {isExpRound ? <CaretUp className="w-3 h-3 text-[#455561]" /> : <CaretDown className="w-3 h-3 text-[#455561]" />}
                      </div>
                    </button>
                    {isExpRound && (
                      <div className="divide-y divide-[#F4F6F5]">
                        {players.map(p => {
                          const isPaid = (r.pot_round_paid || []).includes(p.id);
                          return (
                            <div key={p.id} className="flex items-center justify-between px-3 py-2">
                              <span className="text-xs text-[#1D242B]">{p.name}</span>
                              {isOrganiser ? (
                                <button onClick={() => toggleRoundPaid(r.id, p.id, isPaid)}
                                  className={`text-[10px] font-semibold px-2.5 py-1 rounded-md transition-colors ${isPaid ? 'bg-[#1B4332] text-white' : 'bg-[#F4F6F5] text-[#455561] hover:bg-[#E9EDEB]'}`}
                                  data-testid={`round-paid-${r.id}-${p.id}`}>
                                  {isPaid ? 'Paid' : 'Mark as paid'}
                                </button>
                              ) : (
                                <Badge className={`text-[10px] px-2 py-0.5 ${isPaid ? 'bg-[#1B4332] text-white' : 'bg-[#F4F6F5] text-[#455561]'}`}>
                                  {isPaid ? 'Paid' : 'Unpaid'}
                                </Badge>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Per-Trip Section */}
          {config.per_trip_enabled && (
            <div className="px-4 py-3 space-y-2 border-t border-[#E1E6E4]">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-[#455561] uppercase tracking-wide">Trip Pot — {sym(currency)}{config.per_trip_amount}</h4>
                <span className="text-xs text-[#455561]">
                  {tripPaidCount}/{tripTotal} · {sym(currency)}{tripCollected.toFixed(0)} of {sym(currency)}{tripExpected.toFixed(0)}
                </span>
              </div>
              <div className="border border-[#E1E6E4] rounded-lg overflow-hidden divide-y divide-[#F4F6F5]">
                {players.map(p => {
                  const isPaid = tripPaid.includes(p.id);
                  return (
                    <div key={p.id} className="flex items-center justify-between px-3 py-2">
                      <span className="text-xs text-[#1D242B]">{p.name}</span>
                      {isOrganiser ? (
                        <button onClick={() => toggleTripPaid(p.id, isPaid)}
                          className={`text-[10px] font-semibold px-2.5 py-1 rounded-md transition-colors ${isPaid ? 'bg-[#1B4332] text-white' : 'bg-[#F4F6F5] text-[#455561] hover:bg-[#E9EDEB]'}`}
                          data-testid={`trip-paid-${p.id}`}>
                          {isPaid ? 'Paid' : 'Mark as paid'}
                        </button>
                      ) : (
                        <Badge className={`text-[10px] px-2 py-0.5 ${isPaid ? 'bg-[#1B4332] text-white' : 'bg-[#F4F6F5] text-[#455561]'}`}>
                          {isPaid ? 'Paid' : 'Unpaid'}
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Payout Structure (read-only view) */}
          {!editing && (config.payout_structure || []).length > 0 && (
            <div className="px-4 py-3 space-y-2 border-t border-[#E1E6E4]">
              <h4 className="text-xs font-bold text-[#455561] uppercase tracking-wide">Payout Structure</h4>
              <div className="space-y-1">
                {config.payout_structure.map((p, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5 bg-[#FAFBFA] rounded-md">
                    <span className="text-xs text-[#1D242B]">{p.label || `Position ${i + 1}`}</span>
                    <span className="text-xs font-semibold text-[#1B4332]">{payoutDisplay(p)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Nothing configured message */}
          {!config.per_round_enabled && !config.per_trip_enabled && !editing && (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-[#B8C7C0]">No pots enabled yet</p>
            </div>
          )}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editing} onOpenChange={(open) => { if (!open) setEditing(false); }}>
        <DialogContent className="sm:max-w-md rounded-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Prize Pot Settings</DialogTitle></DialogHeader>
          <div className="space-y-5 py-2">
            {/* Currency */}
            <div>
              <label className="text-xs font-semibold text-[#455561] block mb-1.5">Currency</label>
              <div className="flex gap-2">
                {CURRENCY_OPTIONS.map(c => (
                  <button key={c} onClick={() => setEditCurrency(c)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${editCurrency === c ? 'bg-[#1B4332] text-white' : 'bg-[#F4F6F5] text-[#455561]'}`}>
                    {sym(c)} {c}
                  </button>
                ))}
                <Input value={!CURRENCY_OPTIONS.includes(editCurrency) ? editCurrency : ''}
                  onChange={(e) => setEditCurrency(e.target.value.toUpperCase().slice(0, 3))}
                  placeholder="Other" className="h-8 w-20 text-xs rounded-lg" />
              </div>
            </div>

            {/* Per-Round */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-[#455561]">Per-Round Buy-in</label>
                <Switch checked={editPerRound} onCheckedChange={setEditPerRound} data-testid="edit-per-round-toggle" />
              </div>
              {editPerRound && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[#1D242B]">{sym(editCurrency)}</span>
                  <Input type="text" inputMode="decimal" value={editPerRoundAmt} placeholder="0"
                    onChange={(e) => setEditPerRoundAmt(normaliseAmt(e.target.value.replace(/[^\d.]/g, '')))}
                    className="h-9 w-28 text-sm rounded-lg" data-testid="edit-per-round-amount" />
                  <span className="text-xs text-[#455561]">per player per round</span>
                </div>
              )}
            </div>

            {/* Per-Trip */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-[#455561]">Trip Buy-in</label>
                <Switch checked={editPerTrip} onCheckedChange={setEditPerTrip} data-testid="edit-per-trip-toggle" />
              </div>
              {editPerTrip && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[#1D242B]">{sym(editCurrency)}</span>
                  <Input type="text" inputMode="decimal" value={editPerTripAmt} placeholder="0"
                    onChange={(e) => setEditPerTripAmt(normaliseAmt(e.target.value.replace(/[^\d.]/g, '')))}
                    className="h-9 w-28 text-sm rounded-lg" data-testid="edit-per-trip-amount" />
                  <span className="text-xs text-[#455561]">per player for the trip</span>
                </div>
              )}
            </div>

            {/* Payout Structure */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-[#455561]">Payout Positions</label>
                <Button size="sm" variant="ghost" onClick={addPayout} className="text-xs gap-1 h-7"
                  data-testid="add-payout-btn">
                  <Plus className="w-3 h-3" /> Add
                </Button>
              </div>
              {editPayouts.map((p, i) => (
                <div key={i} className="flex items-start gap-2 p-2 bg-[#FAFBFA] rounded-lg">
                  <div className="flex-1 space-y-1.5">
                    <Input value={p.label} onChange={(e) => updatePayout(i, 'label', e.target.value)}
                      placeholder="e.g. Individual Winner" className="h-8 text-xs rounded-md" />
                    <div className="flex items-center gap-2">
                      <select value={p.scope} onChange={(e) => updatePayout(i, 'scope', e.target.value)}
                        className="h-7 text-[10px] rounded-md border border-[#E1E6E4] bg-white px-1.5">
                        <option value="round">Per Round</option>
                        <option value="trip">Trip</option>
                      </select>
                      <select value={p.type} onChange={(e) => updatePayout(i, 'type', e.target.value)}
                        className="h-7 text-[10px] rounded-md border border-[#E1E6E4] bg-white px-1.5">
                        <option value="fixed">Fixed {sym(editCurrency)}</option>
                        <option value="percent">% of pot</option>
                      </select>
                      <Input type="text" inputMode="decimal" value={p.value || ''} placeholder="0"
                        onChange={(e) => updatePayout(i, 'value', parseFloat(normaliseAmt(e.target.value.replace(/[^\d.]/g, ''))) || 0)}
                        className="h-7 w-20 text-xs rounded-md" />
                    </div>
                  </div>
                  <button onClick={() => removePayout(i)} className="mt-1 text-[#B8C7C0] hover:text-[#D32F2F]">
                    <Trash className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {/* Payout vs expected info banners (non-blocking, per scope) */}
              {(() => {
                const roster = players.length;
                const cs = sym(editCurrency);
                const banners = [];
                // Per-Round check
                if (editPerRound && parseFloat(editPerRoundAmt) > 0) {
                  const expected = roster * parseFloat(editPerRoundAmt);
                  const fixedSum = editPayouts.filter(p => p.scope === 'round' && p.type === 'fixed').reduce((s, p) => s + (p.value || 0), 0);
                  if (fixedSum > 0 && fixedSum !== expected) {
                    const diff = Math.abs(expected - fixedSum);
                    const tag = fixedSum < expected ? `${cs}${diff.toFixed(0)} unallocated` : `${cs}${diff.toFixed(0)} over`;
                    banners.push(<div key="round" className="text-[11px] text-[#96700A] bg-[#FFF8E7] rounded-md px-3 py-1.5" data-testid="payout-info-round">Per-Round payouts: {cs}{fixedSum.toFixed(0)} of {cs}{expected.toFixed(0)} expected ({tag})</div>);
                  }
                }
                // Trip check
                if (editPerTrip && parseFloat(editPerTripAmt) > 0) {
                  const expected = roster * parseFloat(editPerTripAmt);
                  const fixedSum = editPayouts.filter(p => p.scope === 'trip' && p.type === 'fixed').reduce((s, p) => s + (p.value || 0), 0);
                  if (fixedSum > 0 && fixedSum !== expected) {
                    const diff = Math.abs(expected - fixedSum);
                    const tag = fixedSum < expected ? `${cs}${diff.toFixed(0)} unallocated` : `${cs}${diff.toFixed(0)} over`;
                    banners.push(<div key="trip" className="text-[11px] text-[#96700A] bg-[#FFF8E7] rounded-md px-3 py-1.5" data-testid="payout-info-trip">Trip payouts: {cs}{fixedSum.toFixed(0)} of {cs}{expected.toFixed(0)} expected ({tag})</div>);
                  }
                }
                return banners.length > 0 ? <div className="space-y-1.5 mt-1">{banners}</div> : null;
              })()}
            </div>
          </div>
          <DialogFooter className="flex gap-2 mt-2">
            <Button variant="outline" onClick={() => setEditing(false)} className="flex-1 rounded-xl">Cancel</Button>
            <Button onClick={saveConfig} disabled={saving}
              className="flex-1 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl" data-testid="save-pot-btn">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
